// Use relative URLs when NEXT_PUBLIC_API_URL is empty (for nginx proxy in production)
// Default to localhost:8100 for local development
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL !== undefined 
  ? process.env.NEXT_PUBLIC_API_URL 
  : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8100');

export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: `${API_BASE_URL}/api/v1/auth/login/`,
    REFRESH: `${API_BASE_URL}/api/v1/auth/refresh/`,
    VERIFY: `${API_BASE_URL}/api/v1/auth/verify/`,
  },
  PROJECTS: {
    LIST: `${API_BASE_URL}/api/v1/projects/`,
    DETAIL: (id: string | number) => `${API_BASE_URL}/api/v1/projects/${id}/`,
    SEARCH_MEMBER: `${API_BASE_URL}/api/v1/projects/search_member/`,
    CURRENT_USER_MEMBER: `${API_BASE_URL}/api/v1/projects/current_user_member/`,
  },
  TASKS: {
    LIST: `${API_BASE_URL}/api/v1/tasks/`,
    SEARCH: `${API_BASE_URL}/api/v1/tasks/search/`,
  },
  SPRINTS: `${API_BASE_URL}/api/v1/sprints/`,
  NOTIFICATIONS: {
    LIST: `${API_BASE_URL}/api/v1/notifications/`,
    MARK_READ: (id: string | number) => `${API_BASE_URL}/api/v1/notifications/${id}/mark_read/`,
    MARK_ALL_READ: `${API_BASE_URL}/api/v1/notifications/mark_all_read/`,
    UNREAD_COUNT: `${API_BASE_URL}/api/v1/notifications/unread_count/`,
  },
  ACTIVITIES: {
    LIST: `${API_BASE_URL}/api/v1/activities/`,
  },
  ATTACHMENTS: {
    LIST: `${API_BASE_URL}/api/v1/attachments/`,
    DOWNLOAD: (id: string | number) => `${API_BASE_URL}/api/v1/attachments/${id}/download/`,
    DELETE: (id: string | number) => `${API_BASE_URL}/api/v1/attachments/${id}/`,
  },
  BOARDS: `${API_BASE_URL}/api/v1/boards/`,
  LABELS: `${API_BASE_URL}/api/v1/labels/`,
};

export function getAuthHeaders(): Record<string, string> {
  const token = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
  
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
}

export async function refreshToken(): Promise<boolean> {
  const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null;
  
  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch(API_ENDPOINTS.AUTH.REFRESH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    });

    if (response.ok) {
      const data = await response.json();
      if (typeof window !== 'undefined') {
        localStorage.setItem('access_token', data.access);
        if (data.refresh) {
          localStorage.setItem('refresh_token', data.refresh);
        }
      }
      return true;
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
  }
  
  return false;
}

export async function handleAuthError(): Promise<void> {
  // Try to refresh token
  const refreshed = await refreshToken();
  
  if (!refreshed && typeof window !== 'undefined') {
    // Clear tokens and redirect to login
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.location.href = '/login';
  }
}

export async function apiRequest(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  let headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };

  let response = await fetch(endpoint, {
    ...options,
    headers,
  });

  // If 401 and not a login/refresh request, try to refresh token
  if (response.status === 401 && !endpoint.includes('/auth/')) {
    const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('refresh_token') : null;
    if (refreshToken) {
      try {
        const refreshResponse = await fetch(API_ENDPOINTS.AUTH.REFRESH, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh: refreshToken }),
        });

        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          if (typeof window !== 'undefined') {
            localStorage.setItem('access_token', refreshData.access);
          }
          // Retry original request with new token
          headers = {
            ...getAuthHeaders(),
            ...options.headers,
          };
          response = await fetch(endpoint, {
            ...options,
            headers,
          });
        } else {
          // Refresh failed, redirect to login
          console.error('Token refresh failed, redirecting to login.');
          if (typeof window !== 'undefined') {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            window.location.href = '/login';
          }
        }
      } catch (refreshError) {
        console.error('Error refreshing token:', refreshError);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    } else {
      // No refresh token, redirect to login
      console.error('No refresh token found, redirecting to login.');
      if (typeof window !== 'undefined') {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }
  }
  return response;
}

